'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';

/**
 * Props for `PricingComparisonCards`.
 */
export type PricingComparisonCardsProps =
  SliceComponentProps<Content.PricingComparisonCardsSlice>;

/**
 * Component for "PricingComparisonCards" Slices.
 */
const PricingComparisonCards: FC<PricingComparisonCardsProps> = ({ slice }) => {
  // Using hardcoded content for now - can be made dynamic later
  // const { section_title, section_subtitle, pricing_cards } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0e1010] py-20 px-4 w-full flex flex-col items-center"
    >
      <div className="max-w-4xl mx-auto text-center mb-12">
        <h2 className="font-antonio font-bold text-white text-[42px] leading-[1.2] mb-2">
          Transparent &amp; Value-Driven Pricing
        </h2>
        <p className="text-white text-[18px] font-inter font-normal leading-[1.5]">
          Choose the tier that best fits your needs, or customize for enterprise
          scale.
        </p>
      </div>
      <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl justify-center">
        {/* Card 1 */}
        <div
          className="flex-1 rounded-[20px] border border-[#05F273] bg-gradient-to-b from-[#175e5233] to-[#0e101033] p-8 min-w-[320px] max-w-[400px] flex flex-col relative"
          style={{ boxShadow: '0 0 0 1px #05F273' }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
            </div>
            <div className="flex gap-2">
              <span className="w-4 h-4 border border-[#05F273] rounded" />
              <span className="w-4 h-4 border border-[#05F273] rounded flex items-center justify-center relative">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className="absolute inset-0 m-auto"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="3"
                    y1="3"
                    x2="9"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="9"
                    y1="3"
                    x2="3"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex justify-center mb-2">
              {/* Replace with actual icon */}
              <span
                className="w-14 h-14 bg-center bg-contain bg-no-repeat"
                style={{ backgroundImage: 'url(/path/to/standard-icon.svg)' }}
              />
            </div>
            <h3 className="font-antonio font-bold text-white text-[31px] tracking-[-1px] mb-1 leading-normal text-left">
              Standard <br />
              Discovery Report
            </h3>
            <p className="text-white text-[18px] mb-2 leading-[28px] text-left">
              Deep Insights for Mid-Sized Applications.
            </p>
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Ideal for
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Single applications or code repositories up to XX MBs of code.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                What you get
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                A comprehensive discovery report, including detailed tech debt,
                security analysis, modernization recommendations, architectural
                diagrams, and dependency mapping.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Value
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Accelerate your understanding from weeks to hours, reducing
                manual effort by 60-80% and providing a clear roadmap for your
                next steps.
              </p>
            </div>
            <button className="mt-4 w-full bg-[#05F273] text-[#041018] font-manrope font-bold text-[16px] py-3 rounded-[32px] flex items-center justify-center gap-2 hover:bg-[#04d966] transition-colors">
              Get started <span className="inline-block ml-2">→</span>
            </button>
          </div>
        </div>
        {/* Card 2 */}
        <div
          className="flex-1 rounded-[20px] border border-[#05F273] bg-gradient-to-b from-[#175e5233] to-[#0e101033] p-8 min-w-[320px] max-w-[400px] flex flex-col relative"
          style={{ boxShadow: '0 0 0 1px #05F273' }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
            </div>
            <div className="flex gap-2">
              <span className="w-4 h-4 border border-[#05F273] rounded" />
              <span className="w-4 h-4 border border-[#05F273] rounded flex items-center justify-center relative">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className="absolute inset-0 m-auto"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="3"
                    y1="3"
                    x2="9"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="9"
                    y1="3"
                    x2="3"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex justify-center mb-2">
              {/* Replace with actual icon */}
              <span
                className="w-14 h-14 bg-center bg-contain bg-no-repeat"
                style={{ backgroundImage: 'url(/path/to/advanced-icon.svg)' }}
              />
            </div>
            <h3 className="font-antonio font-bold text-white text-[31px] tracking-[-1px] mb-1 leading-normal text-left">
              Advanced <br />
              Discovery Report
            </h3>
            <p className="text-white text-[18px] mb-2 leading-[28px] text-left">
              Unlocking Complex Codebases.
            </p>
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Ideal for
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Larger, more intricate applications or multiple related
                repositories up to xxGB of code.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                What you get
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                All features of the Standard Discovery Report, scaled to handle
                greater complexity, ensuring thorough analysis and actionable
                insights across larger code footprints.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Value
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Gain deeper, faster, and more reliable analysis for substantial
                projects, offering significant cost savings compared to
                traditional consulting approaches.
              </p>
            </div>
            <button className="mt-4 w-full bg-[#05F273] text-[#041018] font-manrope font-bold text-[16px] py-3 rounded-[32px] flex items-center justify-center gap-2 hover:bg-[#04d966] transition-colors">
              Get started <span className="inline-block ml-2">→</span>
            </button>
          </div>
        </div>
        {/* Card 3 */}
        <div
          className="flex-1 rounded-[20px] border border-[#05F273] bg-gradient-to-b from-[#68f5dd66] to-[#0e101066] p-8 min-w-[320px] max-w-[400px] flex flex-col relative"
          style={{ boxShadow: '0 0 0 1px #05F273' }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
              <span className="w-2 h-2 rounded-full bg-[#05F273]" />
            </div>
            <div className="flex gap-2">
              <span className="w-4 h-4 border border-[#05F273] rounded" />
              <span className="w-4 h-4 border border-[#05F273] rounded flex items-center justify-center relative">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className="absolute inset-0 m-auto"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="3"
                    y1="3"
                    x2="9"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="9"
                    y1="3"
                    x2="3"
                    y2="9"
                    stroke="#05F273"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex justify-center mb-2">
              {/* Replace with actual icon */}
              <span
                className="w-14 h-14 bg-center bg-contain bg-no-repeat"
                style={{ backgroundImage: 'url(/path/to/enterprise-icon.svg)' }}
              />
            </div>
            <h3 className="font-antonio font-bold text-white text-[31px] tracking-[-1px] mb-1 leading-normal text-left">
              Enterprise <br />
              Solutions
            </h3>
            <p className="text-white text-[18px] mb-2 leading-[28px] text-left">
              Customized Intelligence for Continuous Transformation
            </p>
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Ideal for
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Organizations with vast portfolios, ongoing modernization
                initiatives, or a need for integrated, recurring insights.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                What you get
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                A fully customized Velx solution, tailored to your unique
                codebase size and organizational requirements. Options for
                continuous discovery-as-a-service, bulk discounts, and dedicated
                support.
              </p>
            </div>
            <div className="h-px w-full bg-[#05F273] opacity-30 mb-2" />
            <div className="mb-2">
              <h4 className="text-[#05F273] font-bold text-[18px] mb-1">
                Value
              </h4>
              <p className="text-white text-[14px] font-light leading-[22px] text-left">
                Optimize your entire software landscape with a predictable
                budget, ensuring continuous agility and sustained competitive
                advantage.
              </p>
            </div>
            <button className="mt-4 w-full bg-[#05F273] text-[#041018] font-manrope font-bold text-[16px] py-3 rounded-[32px] flex items-center justify-center gap-2 hover:bg-[#04d966] transition-colors">
              Get started <span className="inline-block ml-2">→</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingComparisonCards;
